/**
 * Shared validation utilities for the application
 * Provides consistent validation across all forms
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export interface AgeValidationResult extends ValidationResult {
  age?: number;
}

/**
 * Validates email address format
 * Uses comprehensive regex that matches RFC 5322 standards
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email || !email.trim()) {
    return { isValid: false, error: 'Email is required' };
  }

  // Comprehensive email validation regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(email.trim())) {
    return { isValid: false, error: 'Please enter a valid email address (e.g., name@example.com)' };
  }

  return { isValid: true };
};

/**
 * Validates UK phone number format
 * Accepts mobile (07xxx) and landline (01xxx, 02xxx, 03xxx) formats
 * Also accepts international format (+44)
 */
export const validatePhone = (phone: string): ValidationResult => {
  if (!phone || !phone.trim()) {
    return { isValid: false, error: 'Phone number is required' };
  }

  // UK mobile: 07xxx or +44 7xxx
  // UK landline: 01xxx, 02xxx, 03xxx or +44 1xxx, +44 2xxx, +44 3xxx
  const phoneRegex = /^(\+44\s?[1-3,7-9]\d{2,3}|\(?0[1-3,7-9]\d{2,3}\)?)\s?\d{3,4}\s?\d{3,4}$/;
  
  if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
    return { isValid: false, error: 'Please enter a valid UK phone number' };
  }

  return { isValid: true };
};

/**
 * Validates UK postcode format
 * Accepts standard UK postcode patterns (e.g., SW1A 1AA, M1 1AE, B33 8TH)
 */
export const validatePostcode = (postcode: string): ValidationResult => {
  if (!postcode || !postcode.trim()) {
    return { isValid: false, error: 'Postcode is required' };
  }

  // UK postcode regex - matches standard formats
  const postcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
  
  if (!postcodeRegex.test(postcode.trim())) {
    return { isValid: false, error: 'Please enter a valid UK postcode' };
  }

  return { isValid: true };
};

/**
 * Validates age based on date of birth
 * Ensures person is at least 18 years old
 */
export const validateAge = (dob: string, minAge: number = 18): AgeValidationResult => {
  if (!dob) {
    return { isValid: false, error: 'Date of birth is required' };
  }
  
  const birthDate = new Date(dob);
  const today = new Date();
  
  // Check if date is valid
  if (isNaN(birthDate.getTime())) {
    return { isValid: false, error: 'Invalid date of birth' };
  }
  
  // Check if date is in the future
  if (birthDate > today) {
    return { isValid: false, error: 'Date of birth cannot be in the future' };
  }
  
  // Calculate age
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  // Check minimum age
  if (age < minAge) {
    return { isValid: false, age, error: `Must be at least ${minAge} years old` };
  }
  
  // Check maximum reasonable age (e.g., 120 years)
  if (age > 120) {
    return { isValid: false, age, error: 'Please enter a valid date of birth' };
  }
  
  return { isValid: true, age };
};

/**
 * Validates that a field is not empty
 */
export const validateRequired = (value: string, fieldName: string): ValidationResult => {
  if (!value || !value.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  return { isValid: true };
};

/**
 * Validates that a string contains only letters, spaces, and hyphens
 * Useful for names, cities, etc.
 */
export const validateAlphaWithSpaces = (value: string, fieldName: string): ValidationResult => {
  if (!value || !value.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const alphaRegex = /^[a-zA-Z\s\-']+$/;
  
  if (!alphaRegex.test(value)) {
    return { isValid: false, error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
  }

  return { isValid: true };
};

/**
 * Validates UK National Insurance Number format
 */
export const validateNationalInsurance = (nino: string): ValidationResult => {
  if (!nino || !nino.trim()) {
    return { isValid: false, error: 'National Insurance Number is required' };
  }

  // UK NI number format: AB123456C
  const ninoRegex = /^[A-CEGHJ-PR-TW-Z]{1}[A-CEGHJ-NPR-TW-Z]{1}[0-9]{6}[A-D]{1}$/i;
  
  if (!ninoRegex.test(nino.replace(/\s/g, ''))) {
    return { isValid: false, error: 'Please enter a valid National Insurance Number (e.g., AB123456C)' };
  }

  return { isValid: true };
};

/**
 * Formats UK phone number for display
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format based on length
  if (cleaned.startsWith('44')) {
    // International format
    return `+44 ${cleaned.substring(2, 6)} ${cleaned.substring(6)}`;
  } else if (cleaned.startsWith('0')) {
    // UK format
    return `${cleaned.substring(0, 5)} ${cleaned.substring(5)}`;
  }
  
  return phone;
};

/**
 * Formats UK postcode for display (adds space if missing)
 */
export const formatPostcode = (postcode: string): string => {
  const cleaned = postcode.replace(/\s/g, '').toUpperCase();
  
  // Add space before last 3 characters if not present
  if (cleaned.length >= 5 && !cleaned.includes(' ')) {
    return `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`;
  }
  
  return cleaned;
};
